/**
 * Deriving a display first name and avatar initials from a person's full name.
 *
 * The seed carries names with honorifics ("Dr. Luis Alvarez"), and the naïve
 * "first whitespace token" rule greets that learner as "Dr." and initials them
 * as "D" — both read as a bug to the person they name. Stripping a single
 * leading honorific fixes both call sites (the home greeting and the avatar) so
 * they agree, which is why the rule lives in one place.
 */

/**
 * Leading titles we drop before deriving a name. Matched case-insensitively,
 * with or without a trailing period. Deliberately a small, conservative list:
 * over-stripping a real name is worse than leaving an unusual title in, so this
 * only removes tokens that are unambiguously honorifics.
 */
const HONORIFICS = new Set([
  'dr',
  'prof',
  'professor',
  'mr',
  'mrs',
  'ms',
  'mx',
  'miss',
  'sir',
  'dame',
]);

/** Drop one leading honorific token ("Dr. Luis Alvarez" → "Luis Alvarez"). */
export function stripHonorific(name: string): string {
  const trimmed = name.trim();
  const [first, ...rest] = trimmed.split(/\s+/);
  if (first !== undefined && rest.length > 0) {
    const bare = first.replace(/\.$/, '').toLowerCase();
    if (HONORIFICS.has(bare)) return rest.join(' ');
  }
  return trimmed;
}

/** The greeting name: the first real name token, honorific stripped. */
export function firstNameFrom(name: string): string {
  const stripped = stripHonorific(name);
  return stripped.split(/\s+/)[0] ?? name;
}

/**
 * Up to two initials for the avatar placeholder — first and last real name
 * token, honorific stripped ("Dr. Luis Alvarez" → "LA", "Sam Rivera" → "SR").
 * Falls back to the first two characters when there is nothing alphabetic to
 * work with, so the circle is never empty.
 */
export function initialsFrom(name: string): string {
  const words = stripHonorific(name)
    .split(/\s+/)
    .filter((w) => /[A-Za-z]/.test(w));
  if (words.length === 0) return name.trim().slice(0, 2).toUpperCase();
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * A two-letter mark for an INSTITUTION — the square beside a workspace row.
 *
 * Not `initialsFrom`: a person is first-and-last, but "University of Texas
 * School of Dentistry" is not, and first-and-last gives it "UD", which nobody
 * would recognise. The mark is the first two SIGNIFICANT words — an article or
 * preposition ("of", "the", "and") is skipped — so it reads "UT". A leading
 * abbreviation the school already goes by ("UT School of Dentistry", "MIT
 * Media Lab") is kept whole rather than sliced into initials, because that is
 * the mark the people there would draw themselves.
 */
export function markFrom(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => /[A-Za-z]/.test(w));
  const first = words[0];
  if (first !== undefined && /^[A-Z]{2,3}$/.test(first)) return first;
  const significant = words.filter((w) => !SMALL_WORDS.has(w.toLowerCase()));
  const picked = (significant.length > 0 ? significant : words).slice(0, 2);
  if (picked.length === 0) return name.trim().slice(0, 2).toUpperCase();
  return picked.map((w) => w[0]?.toUpperCase() ?? '').join('');
}

/** Words a mark skips over — nobody abbreviates "of". */
const SMALL_WORDS = new Set(['of', 'the', 'and', 'for', 'at', 'in', 'de', 'du', 'la', 'le']);
